package com.advaita.cloud.drive.ui.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import com.advaita.cloud.drive.shared.FileDto
import com.advaita.cloud.drive.shared.FileKind
import com.advaita.cloud.drive.ui.api.CreateTarget
import com.advaita.cloud.drive.ui.api.FilesApi
import com.advaita.cloud.drive.ui.api.userFacingMessage
import com.advaita.cloud.drive.ui.store.ExtractPickerAt
import com.advaita.cloud.drive.ui.store.ExtractToUiState
import com.advaita.cloud.drive.ui.store.MY_DRIVE
import com.advaita.cloud.drive.ui.store.SHARED_WITH_ME
import com.advaita.cloud.drive.ui.store.archiveBaseName
import com.advaita.cloud.drive.ui.store.awaitRegistered
import com.advaita.cloud.drive.ui.store.fullLabel
import com.advaita.cloud.drive.ui.store.into
import com.advaita.cloud.drive.ui.store.isSingleMemberArchive
import com.advaita.cloud.drive.ui.store.label
import com.advaita.cloud.drive.ui.store.onSharedTab
import com.advaita.cloud.drive.ui.store.target
import com.advaita.cloud.drive.ui.store.up
import com.advaita.cloud.drive.ui.store.uploadableSharedFolders
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.isActive
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/**
 * ADV-1529: the body of the extract dialog. It opens compact: one "Extract to" field on the best
 * place the caller may use (beside the archive when the server allows it, else My Drive), and
 * Change. Change opens a folder browser in place, with a My Drive and a Shared with me tab, a
 * back arrow, and New folder. The main button names the place. A place the caller may not use
 * is never offered as a default.
 */
@Composable
internal fun ExtractToBody(state: ExtractToUiState, request: ModalRequest.ExtractTo) {
    LaunchedEffect(state.at, state.browsing) {
        state.loading = true
        state.allowed = null
        try {
            val at = state.at
            // A folder just made in a shared folder answers 404 until the vault registers it.
            val fresh = state.atFreshSharedFolder
            val waiting: (Boolean) -> Unit = { state.registering = it }
            if (state.browsing) {
                state.children = awaitRegistered(fresh, onWaiting = waiting) {
                    when (at) {
                        is ExtractPickerAt.Own -> FilesApi.listFiles(at.path).filter { it.kind == FileKind.FOLDER }
                        ExtractPickerAt.SharedRoots -> uploadableSharedFolders(FilesApi.listShared())
                        is ExtractPickerAt.Shared -> FilesApi.listFolderById(at.trail.last().id).filter { it.kind == FileKind.FOLDER }
                    }
                }
            }
            // The same checks the extract runs. Beside the archive was asked already.
            state.allowed = if (state.atBeside) {
                true
            } else {
                at.target()?.let { t -> awaitRegistered(fresh, onWaiting = waiting) { FilesApi.extractDestinationAllowed(request.file.id, t) } }
            }
        } catch (e: CancellationException) {
            // The person moved on (Choose..., or another folder) while this read ran. The next
            // read owns the state now: write nothing (PR #137 review, finding 2).
            throw e
        } catch (e: Throwable) {
            state.children = emptyList()
            state.error = e.userFacingMessage()
        } finally {
            if (isActive) state.loading = false
        }
    }
    if (state.browsing) FolderBrowser(state) else DestinationField(state)

    val base = archiveBaseName(request.file.displayName)
    // Only where there is a folder to make it in: not before a pick, not on the shared list.
    if (state.chosen && state.at.target() != null) P(attrs = { classes("extract-hint") }) {
        Text(if (isSingleMemberArchive(request.file.displayName)) "Creates the file \"$base\" there." else "Creates the folder \"$base/\" there. The archive is kept.")
    }
    state.error?.let { P(attrs = { classes("extract-note") }) { Text(it) } }
    if (state.allowed == false) {
        P(attrs = { classes("extract-note") }) { Text("You can't add files to \"${state.at.label()}\". Choose another folder.") }
    }
}

@Composable
private fun DestinationField(state: ExtractToUiState) {
    Span(attrs = { classes("modal-label") }) { Text("Extract to") }
    Div(attrs = { classes("extract-dest") }) {
        if (state.chosen) {
            Div(attrs = { classes("extract-dest-field"); attr("title", state.at.fullLabel()) }) {
                Icon(IconName.FOLDER)
                Span(attrs = { classes("extract-dest-name") }) { Text(state.at.fullLabel()) }
            }
        } else {
            // No beside the archive: the caller picks the folder, My Drive included (Tim, 2026-10-05).
            Div(attrs = { classes("extract-dest-field", "empty") }) {
                Icon(IconName.FOLDER)
                Span(attrs = { classes("extract-dest-name") }) { Text("No folder chosen") }
            }
        }
        Button(attrs = {
            classes("btn"); if (!state.chosen) classes("primary")
            onClick { state.browsing = true }
        }) { Text(if (state.chosen) "Change…" else "Choose…") }
    }
}

@Composable
private fun FolderBrowser(state: ExtractToUiState) {
    val at = state.at
    Div(attrs = { classes("extract-tabs") }) {
        Button(attrs = {
            classes("extract-tab"); if (!at.onSharedTab) classes("active")
            onClick { if (at.onSharedTab) state.go(ExtractPickerAt.Own("/")) }
        }) { Text(MY_DRIVE) }
        Button(attrs = {
            classes("extract-tab"); if (at.onSharedTab) classes("active")
            onClick { if (!at.onSharedTab) state.go(ExtractPickerAt.SharedRoots) }
        }) { Text(SHARED_WITH_ME) }
    }
    Div(attrs = { classes("extract-browse-head") }) {
        val up = at.up()
        if (up != null) {
            Button(attrs = { classes("btn", "icon"); attr("aria-label", "Back"); onClick { state.go(up) } }) { Icon(IconName.BACK) }
        }
        Span(attrs = { classes("extract-browse-title") }) { Text(at.label()) }
        if (at.target() != null && state.allowed == true) {
            Button(attrs = {
                classes("btn", "icon")
                attr("aria-label", "New folder")
                attr("title", "New folder")
                onClick { if (state.newFolderName == null) state.newFolderName = "" }
            }) { Icon(IconName.NEW_FOLDER) }
        }
    }
    Div(attrs = { classes("extract-browse-list") }) {
        if (state.newFolderName != null) NewFolderRow(state)
        when {
            state.registering -> Div(attrs = { classes("extract-empty") }) { Text("Preparing the new folder…") }
            state.loading && state.children.isEmpty() -> Div(attrs = { classes("extract-empty") }) { Text("Loading…") }
            state.children.isEmpty() -> Div(attrs = { classes("extract-empty") }) {
                Text(if (at == ExtractPickerAt.SharedRoots) "No shared folder lets you add files." else "No folders here.")
            }
            else -> state.children.forEach { folder -> FolderRow(folder) { state.go(at.into(folder)) } }
        }
    }
}

@Composable
private fun FolderRow(folder: FileDto, onOpen: () -> Unit) {
    Div(attrs = { classes("extract-row"); onClick { onOpen() } }) {
        Icon(IconName.FOLDER)
        Span(attrs = { classes("extract-row-name") }) { Text(folder.displayName) }
        Span(attrs = { classes("chev") }) { Icon(IconName.CHEVRON) }
    }
}

/** A new folder in the place: made through the usual create (the server checks `upload`), then opened. */
@Composable
private fun NewFolderRow(state: ExtractToUiState) {
    val name = state.newFolderName ?: return
    fun create() {
        val target = state.at.target() ?: return
        val trimmed = name.trim()
        if (trimmed.isEmpty() || state.creating) return
        state.creating = true
        MainScope().launch {
            try {
                val made = FilesApi.createFolder(target, trimmed)
                if (target is CreateTarget.SharedFolder) state.freshSharedFolderId = made.id
                state.go(state.at.into(made))
            } catch (e: Throwable) {
                state.error = e.userFacingMessage()
            } finally {
                state.creating = false
            }
        }
    }
    Div(attrs = { classes("extract-row", "editing") }) {
        Icon(IconName.FOLDER)
        Input(InputType.Text, attrs = {
            classes("modal-input", "extract-new-name")
            value(name)
            attr("placeholder", "New folder name")
            ref { element -> element.focus(); onDispose { } }
            onInput { state.newFolderName = it.value }
            onKeyDown { e ->
                when (e.key) {
                    "Enter" -> { e.preventDefault(); e.stopPropagation(); create() }
                    "Escape" -> { e.stopPropagation(); state.newFolderName = null }
                }
            }
        })
        Button(attrs = {
            classes("btn")
            if (name.isBlank() || state.creating) attr("disabled", "")
            onClick { create() }
        }) { Text("Create") }
        Button(attrs = { classes("btn", "icon"); attr("aria-label", "Cancel new folder"); onClick { state.newFolderName = null } }) {
            Icon(IconName.CLOSE)
        }
    }
}
